import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    let rafId: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration, start]);
  return count;
}
function StatItem({
  icon,
  value,
  suffix,
  label,
  animate,
  delay
}: {
  icon: string;
  value: number;
  suffix: string;
  label: string;
  animate: boolean;
  delay: string;
}) {
  const count = useCountUp(value, 2000, animate);
  return <div className={`flex flex-col items-center md:items-start justify-center group reveal-hidden ${animate ? `animate-fade-up ${delay}` : ''}`} data-id="element-598">
      <div className="mb-4 p-0 rounded-full bg-transparent group-hover:bg-white/15 transition-colors" data-id="element-599">
        <img src={icon} alt={label} className="w-10 h-10" data-id="element-600" />
      </div>
      <h3 className="text-3xl md:text-4xl font-bold mb-1 text-white" dir="ltr" data-id="element-601">
        {count}
        {suffix}
      </h3>
      <p className="text-sm md:text-base text-white font-medium" data-id="element-602">{label}</p>
    </div>;
}
export function CareersPage() {
  const {
    t,
    setLocale,
    direction
  } = useLanguage();
  const location = useLocation();
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useScrollReveal();
  const {
    ref: descRef,
    isVisible: descVisible
  } = useScrollReveal();
  const {
    ref: applyRef,
    isVisible: applyVisible
  } = useScrollReveal();
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStatsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    return () => observer.disconnect();
  }, []);
  const stats = [{
    icon: 'https://cdn.magicpatterns.com/uploads/b1z9KUz157t8uvAsuMhbbL/clients.svg',
    value: 100,
    suffix: '+',
    label: t('stats.clients')
  }, {
    icon: 'https://cdn.magicpatterns.com/uploads/aMMC8HfTXK5PT9YJZ3juCy/countries.svg',
    value: 30,
    suffix: '+',
    label: t('stats.countries')
  }, {
    icon: 'https://cdn.magicpatterns.com/uploads/5BZTkasV2vnie4xoAK511C/experience.svg',
    value: 30,
    suffix: '+',
    label: t('stats.experience')
  }, {
    icon: 'https://cdn.magicpatterns.com/uploads/2gPVCGp8VsbvfSj7hCv3s8/employees.svg',
    value: 200,
    suffix: '+',
    label: t('stats.employees')
  }];
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-603">
      <Header variant="dark" data-id="element-604" />

      <main data-id="element-605">
        {/* White header background spacer */}
        <div className="bg-white h-20" data-id="element-606" />

        {/* 1. Hero Banner - same as OtherServicesPage */}
        <section className="gradient-dark-horizontal py-16 md:py-20 relative overflow-hidden" ref={heroRef} aria-label="Careers Hero" data-id="element-607">
          {/* Top-left decorative SVG */}
          <img src="https://cdn.magicpatterns.com/uploads/oSPxnKHLzspYqMxuH2SAJd/inner-top-left.svg" alt="" role="presentation" aria-hidden="true" className="absolute top-0 left-0 w-[200px] md:w-[300px] h-auto pointer-events-none opacity-20" data-id="element-608" />

          <div className="container mx-auto px-6 md:px-12" data-id="element-609">
            <h1 className={`text-white font-bold leading-[1.1] mb-4 uppercase text-[32px] md:text-[44px] lg:text-[50px] reveal-hidden ${heroVisible ? 'animate-fade-up' : ''}`} data-id="element-610">
              {t('careersPage.hero.title')}
            </h1>
            <p className={`text-white/80 text-base md:text-lg whitespace-pre-line leading-relaxed max-w-lg reveal-hidden ${heroVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-611">
              {t('careersPage.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* 2. Description Section - 20% taller, white/light background with bottom-left SVG */}
        <section className="py-20 md:py-28 bg-white relative overflow-hidden" ref={descRef} aria-label="Careers Description" data-id="element-612">
          {/* Bottom-left decorative SVG */}
          <img src="https://cdn.magicpatterns.com/uploads/2yvpQxE97uQiyirfAdGsmc/careers-bottom-left.svg" alt="" role="presentation" aria-hidden="true" className="absolute bottom-0 left-0 w-auto h-[50%] pointer-events-none opacity-35" data-id="element-613" />

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-614">
            <div className="max-w-3xl" data-id="element-615">
              <p className={`text-lg md:text-xl text-[#061d76] leading-relaxed reveal-hidden ${descVisible ? 'animate-fade-up' : ''}`} data-id="element-616">
                {t('careersPage.description')}
              </p>
            </div>
          </div>
        </section>

        {/* 3. Stats Section - same as homepage */}
        <section ref={statsRef} className="gradient-dark-horizontal py-16 md:py-20" dir={direction} data-id="element-617">
          <div className="container mx-auto px-6 md:px-12" data-id="element-618">
            <div className="grid grid-cols-2 md:flex md:justify-between gap-8 text-center md:text-start text-white" data-id="element-619">
              {stats.map((stat, index) => <StatItem key={index} icon={stat.icon} value={stat.value} suffix={stat.suffix} label={stat.label} animate={statsVisible} delay={`delay-${(index + 1) * 100}`} data-id="element-620" />)}
            </div>
          </div>
        </section>

        {/* 4. How to Apply Section - white background */}
        <section className="py-20 md:py-32 bg-white relative overflow-hidden" ref={applyRef} aria-label="How to Apply" data-id="element-621">
          {/* Top-right decorative SVG - reaching middle of section height */}
          <img src="https://cdn.magicpatterns.com/uploads/bHpFuinRQkrvbvA5iECWFY/careers-top-right.svg" alt="" role="presentation" aria-hidden="true" className="absolute top-0 right-0 w-[600px] md:w-[900px] h-[50%] pointer-events-none opacity-35" data-id="element-622" />

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-623">
            <h2 className={`text-[#25B8D6] font-bold text-lg md:text-xl mb-6 reveal-hidden ${applyVisible ? 'animate-fade-up' : ''}`} data-id="element-624">
              {t('careersPage.howToApply.title')}
            </h2>
            <p className={`text-[#061d76] text-2xl md:text-3xl lg:text-4xl font-medium leading-relaxed max-w-4xl reveal-hidden ${applyVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-625">
              {t('careersPage.howToApply.text1')}{' '}
              <a href="mailto:careers@cscgroup.com" className="text-[#25B8D6] hover:underline" data-id="element-626">
                careers@cscgroup.com
              </a>{' '}
              {t('careersPage.howToApply.text2')}
            </p>
          </div>
        </section>
      </main>

      <Footer data-id="element-627" />
    </div>;
}